import { Fragment, useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowRight, Check, Download, PenLine, RotateCcw } from "lucide-react";
import { appPath } from "../api";

/* Homepage figure plates. One illustrative target, paracetamol, is followed from
   input to routes, a step review and the saved record. Every structure is an RDKit
   depiction from public/chemistry/plates (shared bond length per scale). The plates
   explain the workspace; none of them is a search result or a validated procedure. */

const depiction = (file: string) => appPath(`/chemistry/plates/${file}.svg`);
const delay = (seconds: number) => ({ "--reveal-delay": `${seconds}s` }) as CSSProperties;

/** Arms a one-time entrance before first paint; reduced motion keeps plates static (CSS). */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const node = ref.current;
    if (!node || shown) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setShown(true); observer.disconnect(); }
    }, { threshold: .2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);
  return { ref, reveal: shown ? "is-shown" : "is-armed" };
}

const INPUTS = [
  { mode: "Draw", kind: "draw", value: <><PenLine size={17} aria-hidden="true" />Sketch the structure</> },
  { mode: "SMILES", kind: "smiles", value: "CC(=O)Nc1ccc(O)cc1" },
  { mode: "Name", kind: "name", value: "paracetamol" },
];

export function TargetInputVisual() {
  const { ref, reveal } = useReveal<HTMLDivElement>();
  return <div ref={ref} className={`plate plate--target ${reveal}`} role="img" aria-label="Three ways to enter a target: draw it, paste the SMILES CC(=O)Nc1ccc(O)cc1, or type the name paracetamol. Each resolves to the same molecular structure, C8H9NO2, drawn with RDKit.">
    <div className="target-inputs" aria-hidden="true">
      <p className="plate-label">Input</p>
      <ol>
        {INPUTS.map((input, index) => <li key={input.mode} data-reveal style={delay(index * .1)}>
          <span className="target-input-mode">{input.mode}</span>
          <span className={`target-input-value target-input-value--${input.kind}`}>{input.value}</span>
        </li>)}
      </ol>
      <span className="target-resolves" data-reveal style={delay(.3)}><ArrowRight size={16} /></span>
    </div>
    <div className="target-resolved" aria-hidden="true">
      <p className="plate-label">Resolved target</p>
      <div className="target-canvas"><img src={depiction("paracetamol-lg")} width="315" height="152" alt="" loading="lazy" decoding="async" data-reveal style={delay(.4)} /></div>
      <div className="target-identity"><strong>Paracetamol</strong><span>C<sub>8</sub>H<sub>9</sub>NO<sub>2</sub> · 151.16 g/mol</span></div>
    </div>
  </div>;
}

type RouteId = 1 | 2 | 3;
type NodeId = "nitrobenzene" | "phenol" | "nitrophenol" | "acetophenone" | "aminophenol" | "oxime" | "paracetamol";
type RouteNodeSpec = { name: string[]; file: string; w: number; h: number; x: number; y: number; routes: RouteId[]; tag?: string; column: number };

/** Intrinsic sizes match the light depictions, so every structure keeps one bond length. */
const NODES: Record<NodeId, RouteNodeSpec> = {
  nitrobenzene: { name: ["Nitrobenzene"], file: "nitrobenzene", w: 95, h: 57, x: 0, y: 0, routes: [2], column: 0 },
  phenol: { name: ["Phenol"], file: "phenol", w: 90, h: 51, x: 0, y: 156, routes: [1, 3], tag: "SHARED START", column: 0 },
  nitrophenol: { name: ["4-Nitrophenol"], file: "4-nitrophenol", w: 129, h: 57, x: 314, y: 156, routes: [1], column: 1 },
  acetophenone: { name: ["4-Hydroxyacetophenone"], file: "4-hydroxyacetophenone", w: 129, h: 53, x: 314, y: 312, routes: [3], column: 1 },
  aminophenol: { name: ["4-Aminophenol"], file: "4-aminophenol", w: 127, h: 51, x: 628, y: 156, routes: [1, 2], tag: "SHARED INTERMEDIATE", column: 2 },
  oxime: { name: ["4-Hydroxyacetophenone", "oxime"], file: "4-hydroxyacetophenone-oxime", w: 159, h: 53, x: 628, y: 312, routes: [3], column: 2 },
  paracetamol: { name: ["Paracetamol"], file: "paracetamol", w: 150, h: 74, x: 920, y: 222, routes: [1, 2, 3], tag: "TARGET", column: 3 },
};
const EDGES: { routes: RouteId[]; label: string; d: string; lx: number; ly: number; anchor?: "end"; column: number }[] = [
  { routes: [2], label: "Reduction, rearrangement", d: "M196 64H712C720 64 726 70 726 78V155", lx: 460, ly: 54, column: 0 },
  { routes: [1], label: "Nitration", d: "M196 220H313", lx: 255, ly: 208, column: 0 },
  { routes: [3], label: "Acylation", d: "M196 220C255 220 255 376 313 376", lx: 270, ly: 362, anchor: "end", column: 0 },
  { routes: [1], label: "Reduction", d: "M510 220H627", lx: 569, ly: 208, column: 1 },
  { routes: [3], label: "Oximation", d: "M510 376H627", lx: 569, ly: 364, column: 1 },
  { routes: [1, 2], label: "Acetylation", d: "M824 220C872 220 872 298 919 298", lx: 868, ly: 208, column: 2 },
  { routes: [3], label: "Beckmann", d: "M824 376C872 376 872 298 919 298", lx: 868, ly: 398, column: 2 },
];
const ROUTES: { id: RouteId; summary: string; chain: NodeId[]; steps: string[] }[] = [
  { id: 1, summary: "via 4-nitrophenol", chain: ["phenol", "nitrophenol", "aminophenol", "paracetamol"], steps: ["Nitration", "Reduction", "Acetylation"] },
  { id: 2, summary: "from nitrobenzene", chain: ["nitrobenzene", "aminophenol", "paracetamol"], steps: ["Reduction, rearrangement", "Acetylation"] },
  { id: 3, summary: "via Beckmann rearrangement", chain: ["phenol", "acetophenone", "oxime", "paracetamol"], steps: ["Acylation", "Oximation", "Beckmann rearrangement"] },
];

function RouteTile({ id, focus }: { id: NodeId; focus: RouteId }) {
  const node = NODES[id];
  const target = id === "paracetamol";
  const [w, h] = target ? [216, 152] : [196, 128];
  const areaTop = node.tag ? 32 : 16;
  const areaHeight = h - areaTop - (node.name.length > 1 ? 50 : 40);
  const nameY = h - (node.name.length > 1 ? 34 : 22);
  return <g transform={`translate(${node.x} ${node.y})`}>
    <g className={`route-tile${node.routes.includes(focus) ? " is-focus" : ""}${target ? " route-tile--target" : ""}`} data-reveal="fade" style={delay(.1 + node.column * .16)}>
      <rect width={w} height={h} rx="10" />
      {node.tag && <text className="route-tile-tag" x="14" y="23">{node.tag}</text>}
      <image href={depiction(`${node.file}-light`)} x={(w - node.w) / 2} y={areaTop + (areaHeight - node.h) / 2} width={node.w} height={node.h} />
      <text className="route-tile-name" x={w / 2} y={nameY} textAnchor="middle">{node.name.map((line, index) => <tspan key={line} x={w / 2} dy={index ? 17 : 0}>{line}</tspan>)}</text>
    </g>
  </g>;
}

function routeNote(id: NodeId, focus: RouteId) {
  if (id === "paracetamol") return "Target";
  const others = NODES[id].routes.filter(route => route !== focus);
  return others.length ? `Shared with route ${others.join(" and ")}` : "";
}

/** Wide screens show the connected landscape; narrow screens read the focused route as a list. */
export function RouteLandscapeVisual() {
  const { ref, reveal } = useReveal<HTMLDivElement>();
  const [focus, setFocus] = useState<RouteId>(1);
  const route = ROUTES[focus - 1];
  return <div ref={ref} className={`plate plate--routes ${reveal}`}>
    <div className="route-toolbar" role="group" aria-label="Route in focus">
      {ROUTES.map(option => <button key={option.id} type="button" aria-pressed={focus === option.id} onClick={() => setFocus(option.id)}>
        <span className="route-choice-name">Route {option.id}<span>{option.steps.length} steps</span></span>
        <span className="route-choice-summary">{option.summary}</span>
      </button>)}
    </div>
    <div className="route-map" role="img" aria-label={`Conceptual route landscape for paracetamol. Route 1 nitrates phenol, reduces 4-nitrophenol and acetylates 4-aminophenol. Route 2 reaches the same 4-aminophenol from nitrobenzene by reduction and rearrangement, then acetylates it. Route 3 acylates phenol, forms the oxime and ends with a Beckmann rearrangement. Route ${focus} is in focus. This is an illustration, not a proposed reaction.`}>
      <svg className="route-map-canvas" viewBox="0 0 1136 440" fill="none" aria-hidden="true">
        <defs>
          <marker id="route-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" markerUnits="userSpaceOnUse" orient="auto"><path d="M2 1.5L9 5L2 8.5" fill="none" stroke="#3a5486" strokeWidth="1.6" /></marker>
          <marker id="route-arrow-focus" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" markerUnits="userSpaceOnUse" orient="auto"><path d="M2 1.5L9 5L2 8.5" fill="none" stroke="#50cdfa" strokeWidth="1.6" /></marker>
        </defs>
        {EDGES.map(edge => {
          const inFocus = edge.routes.includes(focus);
          return <g key={edge.d} data-reveal="fade" style={delay(.3 + edge.column * .16)}>
            <path className={`route-edge${inFocus ? " is-focus" : ""}`} d={edge.d} />
            <text className={`route-edge-label${inFocus ? " is-focus" : ""}`} x={edge.lx} y={edge.ly} textAnchor={edge.anchor ?? "middle"}>{edge.label}</text>
          </g>;
        })}
        {(Object.keys(NODES) as NodeId[]).map(id => <RouteTile key={id} id={id} focus={focus} />)}
      </svg>
      <div className="route-chain" key={focus} aria-hidden="true">
        {route.chain.map((id, index) => <Fragment key={id}>
          {index > 0 && <div className="route-chain-step"><ArrowRight size={18} /><span>{route.steps[index - 1]}</span></div>}
          <div className={`route-chain-node${id === "paracetamol" ? " is-target" : ""}`}>
            <img src={depiction(`${NODES[id].file}-light`)} width={NODES[id].w} height={NODES[id].h} alt="" loading="lazy" decoding="async" />
            <div><strong>{NODES[id].name.join(" ")}</strong>{routeNote(id, focus) && <span>{routeNote(id, focus)}</span>}</div>
          </div>
        </Fragment>)}
      </div>
    </div>
    <p className="visually-hidden" aria-live="polite">{`Route ${focus} in focus: ${route.summary}, ${route.steps.length} steps.`}</p>
  </div>;
}

const CONDITIONS = [
  { field: "Solvent", state: "exact", symbol: "✓", label: "Reported · exact reaction record" },
  { field: "Temperature", state: "analogue", symbol: "≈", label: "From an analogue · not this substrate" },
  { field: "Time", state: "missing", symbol: "?", label: "Not supplied" },
  { field: "Atmosphere", state: "missing", symbol: "?", label: "Not supplied" },
];

/** Field states use the workspace's review labels and carry no invented values. */
export function StepEvidenceVisual() {
  const { ref, reveal } = useReveal<HTMLDivElement>();
  return <div ref={ref} className={`plate plate--evidence ${reveal}`} role="img" aria-label="Illustrative step review: 4-aminophenol and acetic anhydride give paracetamol. The solvent is reported in an exact reaction record, the temperature comes from an analogue and is labelled as not this substrate, and time and atmosphere are not supplied, so those gaps stay visible.">
    <div className="evidence-step" aria-hidden="true">
      <p className="plate-label">Route 1 · Step 3 of 3</p>
      <strong className="evidence-step-title">N-Acetylation</strong>
      <div className="evidence-equation" data-reveal style={delay(.05)}>
        <figure><img src={depiction("4-aminophenol-md")} width="156" height="61" alt="" loading="lazy" decoding="async" /><figcaption>4-Aminophenol</figcaption></figure>
        <span className="evidence-plus">+</span>
        <figure><img src={depiction("acetic-anhydride-md")} width="113" height="61" alt="" loading="lazy" decoding="async" /><figcaption>Acetic anhydride</figcaption></figure>
        <span className="evidence-arrow"><svg viewBox="0 0 64 12" fill="none"><path d="M0 6H62M56 1L62 6L56 11" stroke="currentColor" strokeWidth="1.5" /></svg></span>
        <figure><img src={depiction("paracetamol-md")} width="185" height="91" alt="" loading="lazy" decoding="async" /><figcaption>Paracetamol</figcaption></figure>
      </div>
    </div>
    <div className="evidence-fields" aria-hidden="true">
      <p className="plate-label">Conditions</p>
      <dl>{CONDITIONS.map((row, index) => <div key={row.field} data-state={row.state} data-reveal style={delay(.25 + index * .1)}>
        <dt>{row.field}</dt>
        <dd><span className="evidence-symbol">{row.symbol}</span>{row.label}</dd>
      </div>)}</dl>
      <p className="evidence-note">Values from another substrate are labelled one by one. Missing values stay missing.</p>
    </div>
  </div>;
}

const HISTORY = [
  { name: "Paracetamol", file: "paracetamol-sm", w: 86, h: 44, meta: "Drawn structure · 3 routes", when: "Today", active: true },
  { name: "Aspirin", file: "aspirin-sm", w: 69, h: 57, meta: "From SMILES · 2 routes", when: "Yesterday" },
  { name: "Caffeine", file: "caffeine-sm", w: 70, h: 59, meta: "By name · 4 routes", when: "Last week" },
];

export function HistoryVisual() {
  const { ref, reveal } = useReveal<HTMLDivElement>();
  return <div ref={ref} className={`plate plate--history ${reveal}`} role="img" aria-label="Illustrative search history with paracetamol, aspirin and caffeine. The paracetamol search is reopened with its routes.">
    <div className="plate-head" aria-hidden="true"><p className="plate-label">History</p><span>Saved searches</span></div>
    <ul aria-hidden="true">{HISTORY.map((item, index) => <li key={item.name} className={item.active ? "is-active" : undefined} data-reveal style={delay(index * .1)}>
      <span className="history-thumb"><img src={depiction(item.file)} width={item.w} height={item.h} alt="" loading="lazy" decoding="async" /></span>
      <span className="history-text"><strong>{item.name}</strong><span>{item.meta}</span></span>
      <span className="history-when">{item.active ? <><RotateCcw size={14} />Reopened</> : item.when}</span>
    </li>)}</ul>
  </div>;
}

const REVIEW_CHAIN = [
  { file: "phenol-sm", w: 53, h: 31 },
  { file: "4-nitrophenol-sm", w: 75, h: 35 },
  { file: "4-aminophenol-sm", w: 75, h: 31 },
  { file: "paracetamol-sm", w: 86, h: 44 },
];

/** Mirrors the workspace export (blitzsynth-R01.json, schema blitzsynth.route-review.v1); never a procedure. */
export function ReviewExportVisual() {
  const { ref, reveal } = useReveal<HTMLDivElement>();
  return <div ref={ref} className={`plate plate--export ${reveal}`} role="img" aria-label="Illustrative route review export for paracetamol, route 1, saved as a JSON review record: the target and every step of the route, the available conditions and sources, and missing evidence left unfilled. It is not a validated procedure.">
    <div className="plate-head" aria-hidden="true"><p className="plate-label">Route review</p><span className="export-file"><Download size={14} />blitzsynth-R01.json</span></div>
    <div className="export-body" aria-hidden="true">
      <div className="export-title"><strong>Paracetamol · Route 1</strong><span>3 steps</span></div>
      <div className="export-scheme" data-reveal style={delay(.05)}>{REVIEW_CHAIN.map((item, index) => <Fragment key={item.file}>
        {index > 0 && <ArrowRight size={14} />}
        <img src={depiction(item.file)} width={item.w} height={item.h} alt="" loading="lazy" decoding="async" />
      </Fragment>)}</div>
      <ul className="export-contents">{["Target and every step of the route", "Available conditions and sources", "Missing evidence left unfilled"].map((item, index) => <li key={item} data-reveal style={delay(.2 + index * .1)}><Check size={15} />{item}</li>)}</ul>
      <p className="export-note">Review record · not a validated procedure</p>
    </div>
  </div>;
}
